import { useEffect, useState } from 'react'
import type { MeResponse, Rule } from '@alerts/shared'
import { Home } from './pages/Home'
import { NoAccess } from './pages/NoAccess'
import { Wizard } from './pages/Wizard'
import { api } from './lib/api'
import { confirmAction, initTg } from './lib/tg'

type View =
  | { kind: 'loading' }
  | { kind: 'no-access' }
  | { kind: 'home'; me: MeResponse }
  | { kind: 'wizard'; me: MeResponse; market: string | null; existing: Rule | null }
  | { kind: 'error'; message: string }

export function App() {
  const [view, setView] = useState<View>({ kind: 'loading' })

  async function refresh() {
    try {
      const me = await api.me()
      if (!me.hasAccess) setView({ kind: 'no-access' })
      else setView({ kind: 'home', me })
    } catch (e) {
      setView({ kind: 'error', message: String(e) })
    }
  }

  useEffect(() => {
    initTg()
    refresh()
  }, [])

  if (view.kind === 'loading') return <div className="app"><p>Loading…</p></div>
  if (view.kind === 'error') {
    const isMissingInitData = view.message.includes('missing initData')
    return (
      <div className="app">
        {isMissingInitData ? (
          <>
            <p>Не удалось получить данные авторизации Telegram.</p>
            <p>Откройте приложение через кнопку <b>Open Alerts</b> рядом с полем ввода в чате с ботом, либо обновите Telegram до последней версии.</p>
          </>
        ) : (
          <p>Error: {view.message}</p>
        )}
      </div>
    )
  }
  if (view.kind === 'no-access') return <NoAccess />

  if (view.kind === 'home') {
    return (
      <Home
        me={view.me}
        onEdit={(rule) => setView({ kind: 'wizard', me: view.me, market: rule.market, existing: rule })}
        onAddMarket={() => setView({ kind: 'wizard', me: view.me, market: null, existing: null })}
        onToggleEnabled={async (rule, enabled) => {
          await api.setRuleEnabled(rule.market, enabled)
          refresh()
        }}
        onDelete={async (rule) => {
          if (!(await confirmAction(`Delete ${rule.market} rule?`))) return
          await api.deleteRule(rule.market)
          refresh()
        }}
      />
    )
  }

  if (view.kind === 'wizard') {
    const mode = view.market !== null
      ? ({ kind: 'edit-one', market: view.market, existing: view.existing } as const)
      : ({ kind: 'add-multi' } as const)

    return (
      <Wizard
        mode={mode}
        config={view.me.config}
        existingRules={view.me.rules}
        onCancel={() => refresh()}
        onSubmit={async (results) => {
          await Promise.all(results.map((r) => api.saveRule(r)))
          await refresh()
        }}
      />
    )
  }

  return null
}
